import { toCsv } from '@/lib/csv';
import { downloadTextFile } from '@/lib/downloadTextFile';

import type { CoachMessage } from './kaiCoachService';

// One activity/session column-group in the export. Built from a CoachThread; the
// builder only needs the display header, the unit its score is stored under, and
// the points it is worth.
export interface CoachCsvActivity {
  header: string;
  points: number;
  scoreUnitUid: string;
}

export interface CoachCsvStudent {
  email: string;
  firstName: string;
  instituteId: string;
  lastName: string;
  // Points earned per scoreUnitUid (from the student's section_stats).
  earnedByUnit: Record<string, number>;
  // Present only when the instructor opted to include conversation history:
  // the student's transcript per scoreUnitUid.
  conversationsByUnit?: Record<string, CoachMessage[]>;
  // Goal-reached coaches: when each unit's goal was reached (null: not yet) and
  // Kai's evidence — from the awarding assessment, else the latest one.
  goalsByUnit?: Record<string, CoachCsvGoal>;
}

export interface CoachCsvGoal {
  evidence: string;
  reachedAtMillis: number | null;
}

// Which optional column groups the export carries.
export interface CoachCsvColumns {
  conversations: boolean;
  goals: boolean;
}

// The award time as a spreadsheet can sort it: local date and 24-hour time.
export function formatGoalReachedAt(millis: number | null): string {
  if (millis === null || !Number.isFinite(millis)) {
    return '';
  }
  const date = new Date(millis);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Kai's goal record for one thread, read from its messages: when the goal was
 * reached (the conversation's award time) and the evidence of the assessment
 * that awarded it, or of the latest assessment when it has not been reached.
 */
export function coachGoalFromConversation(
  messages: CoachMessage[],
  reachedAtMillis: number | null,
  reachedMessageId: string,
): CoachCsvGoal {
  const awarding = reachedMessageId
    ? messages.find((message) => message.id === reachedMessageId)
    : undefined;
  const latest = [...messages].reverse().find((message) => message.assessment);
  return {
    evidence: (awarding?.assessment ?? latest?.assessment)?.evidence ?? '',
    reachedAtMillis,
  };
}

const STUDENT_HEADERS = ['Last Name', 'First Name', 'Email', 'ID', 'Total Score'];

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return '0';
  }
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(4)));
}

// Renders a transcript as a single readable multi-line cell: one turn per
// paragraph, prefixed by speaker, with a blank line between turns. toCsv quotes
// embedded newlines so Sheets/Excel keep it inside one cell.
export function formatConversation(messages: CoachMessage[] | undefined): string {
  if (!messages || messages.length === 0) {
    return '';
  }
  return messages
    .map((message) => {
      const speaker = message.role === 'assistant' ? 'Kai' : 'Student';
      const body = message.blocks
        .map((block) => block.markdown?.trim() ?? '')
        .filter((text) => text.length > 0)
        .join('\n\n');
      return `${speaker}: ${body}`.trimEnd();
    })
    .join('\n\n');
}

function activityHeaders(activity: CoachCsvActivity, columns: CoachCsvColumns): string[] {
  const headers = [`${activity.header} Score`];
  if (columns.goals) {
    headers.push(`${activity.header} Goal reached`, `${activity.header} Kai's assessment`);
  }
  if (columns.conversations) {
    headers.push(`${activity.header} Conversation`);
  }
  return headers;
}

function activityMaxCells(activity: CoachCsvActivity, columns: CoachCsvColumns): string[] {
  const cells = [formatNumber(activity.points)];
  if (columns.goals) {
    cells.push('', '');
  }
  if (columns.conversations) {
    cells.push('');
  }
  return cells;
}

function activityStudentCells(
  activity: CoachCsvActivity,
  student: CoachCsvStudent,
  columns: CoachCsvColumns,
): string[] {
  const cells = [formatNumber(student.earnedByUnit[activity.scoreUnitUid] ?? 0)];
  if (columns.goals) {
    const goal = student.goalsByUnit?.[activity.scoreUnitUid];
    cells.push(formatGoalReachedAt(goal?.reachedAtMillis ?? null), goal?.evidence ?? '');
  }
  if (columns.conversations) {
    cells.push(
      formatConversation(student.conversationsByUnit?.[activity.scoreUnitUid]),
    );
  }
  return cells;
}

function toColumns(columns: boolean | CoachCsvColumns): CoachCsvColumns {
  return typeof columns === 'boolean' ? { conversations: columns, goals: false } : columns;
}

function studentTotal(
  student: CoachCsvStudent,
  activities: CoachCsvActivity[],
): number {
  return activities.reduce(
    (sum, activity) => sum + (student.earnedByUnit[activity.scoreUnitUid] ?? 0),
    0,
  );
}

// columns: which optional groups to include; a boolean means conversations only
// (the export's original signature).
export function buildCoachScoresRows(
  activities: CoachCsvActivity[],
  students: CoachCsvStudent[],
  columnsOrConversations: boolean | CoachCsvColumns,
): string[][] {
  const columns = toColumns(columnsOrConversations);
  const header = [
    ...STUDENT_HEADERS,
    ...activities.flatMap((activity) => activityHeaders(activity, columns)),
  ];

  const totalMax = activities.reduce((sum, activity) => sum + activity.points, 0);
  const maxRow = [
    'Max Score',
    '',
    '',
    '',
    formatNumber(totalMax),
    ...activities.flatMap((activity) => activityMaxCells(activity, columns)),
  ];

  const studentRows = students.map((student) => [
    student.lastName,
    student.firstName,
    student.email,
    student.instituteId,
    formatNumber(studentTotal(student, activities)),
    ...activities.flatMap((activity) => activityStudentCells(activity, student, columns)),
  ]);

  return [header, maxRow, ...studentRows];
}

export function buildCoachScoresFilename(moduleTitle: string): string {
  const safe = moduleTitle
    .replace(/[/\\:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return `${safe || 'kai coach'} - detailed scores.csv`;
}

export function exportCoachDetailedScoresCsv(
  moduleTitle: string,
  activities: CoachCsvActivity[],
  students: CoachCsvStudent[],
  columns: boolean | CoachCsvColumns,
): void {
  const csv = toCsv(buildCoachScoresRows(activities, students, columns));
  downloadTextFile(buildCoachScoresFilename(moduleTitle), csv);
}
